import GameManager from '../../cvp-webgames-core/GameManager';
import BaseScenarioPart from '../../cvp-webgames-core/Scenario/BaseScenarioPart';
import { VideoEnvironmentProps } from '../../cvp-webgames-core/Components/Environments/React/VideoEnvironment';
import { DaarcBaseScenario } from '../DaarcBaseScenario';
import BasePracticeScenario from '../base-practice-scenario/BasePracticeScenario';
import { constants } from '../../misc/constants';
import { DebriefIntro, DebriefVortex } from '../../components/ui/DebriefUI';
import { AssessAirwayActionData, AssessAirwayActions } from '../../components/ui/AssessAirwayUI';
import { SuccessType } from '../general/general-scenario-stuff';
import { Windows } from '../../misc/tools';
import { VortexUICurrentAttempt } from '../../components/ui/hud/VortexUI';
import EttSpecialUI from '../../components/ui/EttSpecialUI';
import BaseTestScenario from '../base-test-scenario/BaseTestScenario';

export default class AndreGarza extends BaseTestScenario {
	public eTTCorrect: boolean;
	public isForceps: boolean;
	public forcepsText: boolean;
	
	public get maxScore(): number {
		return 53100;
	}

	public get predeterminedPoints(): number {
		var assessPatientRemoveDenturesPoints = 500;
		var setupFiberOpticsPoints = 1000;
		var cricPoints = 2400;
		var correctAttempt = 32700;

		return assessPatientRemoveDenturesPoints +
			setupFiberOpticsPoints +
			cricPoints +
			correctAttempt;
	}

	public get totalNumberOfQuestions(): number {
		return 5;
	}

	public get currentLevel(): number {
		return 5;
	}

	public showDefaultSideMenu() {
		const context = this;
		context.sideMenuId = context.uiWrapper.displaySideMenu({
			gameManager: this.gameManager,
			inductionTimeInSeconds: 30,
			apneaTimeInSeconds: 210,
			bloodPressureNumerator: 150,
			bloodPressureDenominator: 90,
			bloodPressureMinNumerator: 50,
			bloodPressureMinDenominator: 33,
			bloodPressureMaxNumerator: 220,
			bloodPressureMaxDenominator: 110,
			sa02: 89,
			heartRate: 90,
			heartRateMax: 150,
			respirationRate: 32,
			resetEtc02: false,
			etc02: 35,
			showETC02: false,
			isApneaTimerIdle: true,
			onInductionTimerRanOut: () => {
				context.hasInductionTimerRanOut = true;
			},
			onApneaTimerRanOut: () => {
                context.failedScenario = true;
				this._CheckOxy();
				context.onShowSuccessFailedAttempt(constants.attemptText.failedTitle, 'You did not establish a stable, patent airway in time for Andre Garza. He has expired. Review the debrief and then try again.', true);
			}
		}, context.sideMenuId);
	}

	constructor(gameManager: GameManager, scenarioName: string) {
		super(gameManager, scenarioName);

		this._setupIntro();
		this._setupGeneralNeutralState();
		this._setupProcedures();
	}

	public getDebriefIntro(): DebriefIntro {
		return {
			items: [
				'Intubating is the optimal option due to the possibility of trauma and edema from a retained foreign body',				
				'Did you factor in the clinical condition of the patient in your utilization of the Vortex approach?'
			],
			link: 'https://google.com'
		};
	}

	public getDebriefVortex(): DebriefVortex {
		return {
			successItems: [
				'BVM: ' + this.bvmAttemptCount + ' attempt(s)',
				'ETT: ' + this.ettAttemptCount + ' attempt(s)',
				'SGA: ' + this.sgaAttemptCount + ' attempt(s)',
				'Cricothyrotomy: ' + this.cricAttemptCount + ' attempt(s)'
			],
			askYourselfItems: [
				'Did you exceed the maximum number of optimizations (3) for a given attempt?',
				'Did you optimize between each attempt at the bag valve mask, intubation and supraglottic airway?',
				'Did you follow the Vortex approach by not returning to a technique that you had already attempted with appropriate optimizations and failed?',
				'How can you more efficiently manage the airway? Remember that the quicker you establish a stable, patent airway, the better the patient outcome.'
			]
		};
	}

	public onBVMFinished(): void {
		//commenting as bvm is not a procedure that happens for this scenario...
		// this.showFailedAttemptPopUpAfterAuscultate(VortexUICurrentAttempt.BVM, 'A patent airway has not been achieved. Continue attempts at managing the airway.', 'Failed Attempt');
	}

	public onETTFinished(): void {
		if(this.eTTCorrect){
			this.addScore(this.predeterminedPoints);
			this._CheckOxy();
			this.showSuccessPopUpAfterAuscultate(VortexUICurrentAttempt.ETT, 'You established a stable, patent airway for Andre Garza by using the Magill forceps to remove the denture in his throat and then intubating him.  Mr. Garza was apneic for ' + this.apneaTimeElapsed + ' while you managed his airway. You earned ' + this.totalScore + ' out of a possible ' + this.maxScore + ' points. Review the debrief for a summary of key learning points.', 'Good Job on this Scenario'); // Use Forceps to remove dentures
		} else {
			this.showFailedAttemptPopUpAfterAuscultate(VortexUICurrentAttempt.ETT, 'A patent airway has not been achieved. Continue attempts at managing the airway.', 'Failed Attempt' );
		}		
	}

	public onSGAFinished(): void {
		this.showFailedAttemptPopUpAfterAuscultate(VortexUICurrentAttempt.SGA, 'A patent airway has not been achieved. Continue attempts at managing the airway.', 'Failed Attempt');
	}

	public onCricFinished(): void {
		this._CheckOxy();
		this.debriefLogFailedItem("You should not have attempted a cricothyrotomy.");
		this.showFailedScenarioPopUpAfterAuscultate(VortexUICurrentAttempt.Cric, 'You should not have attempted a cricothyrotomy. A stable, patent airway can be established through non-surgical attempts. Review the debrief and then try again.', 'Failed Scenario');
		
	}

	public _CheckOxy() {
		if(!this.preOxDone) {
			this.debriefLogFailedItem("Pre-Oxygenation: You did not pre-oxygenate the patient.")
		};
		if(!this.callFlexBr) {
			this.debriefLogFailedItem("Call for flexible bronchoscope: Recognizing the need for a flexible bronchoscope early is critical, especially when the patient’s condition indicates intubation and use of a supraglottic airway will be anatomically impossible.")
		}	
	}

	public getAssessAirwayData(): AssessAirwayActionData[] {
		return [
			{
				relatedAction: AssessAirwayActions.ExamineNeck,
				title: 'Examine Neck',
				text: 'His larynx is free and mobile. You can palpate the thyroid and cricoid cartilage, as well as the cricothyroid membrane.'
			},
			{
				relatedAction: AssessAirwayActions.LiftChinToCeiling,
				title: 'Lift chin to ceiling',
				text: 'Thyromental Distance: 3 finger breadths (4 cm)<br />Hyoid to Thyroid Notch Distance: 2 finger breadths (3 cm)'
			},
			{
				relatedAction: AssessAirwayActions.PutChinToChest,
				title: 'Put chin to chest',
				text: 'You see the patient moving his chin to his chest.'
			},
			{
				relatedAction: AssessAirwayActions.RemoveDentures,
				title: 'Remove Dentures',
				text: 'No dentures to remove.',
				noPoints: true
			},
			{
				relatedAction: AssessAirwayActions.ShowTeeth,
				title: 'Smile for me', //Patient redponds "I have no teeth. And my upper denture is missing."
				text: 'Patient has dentures',
				
			},
			{
				relatedAction: AssessAirwayActions.StickOutTongue,
				imgUrl: './assets/images/assess-airway-images/Class4DenturePeekingOut.png',
				title: 'Open mouth',
				text: 'Inter-incisor Distance: 3 finger breadths (4 cm)'
			}
		]
	}

	private _setupIntro() {
		// this.intro.config.part1Config = {
		// 	videoUrl: './assets/videos/scenario3/daarc-scenario3-andregarza-v1.mp4',
		// 	dialogs: [
		// 		{
		// 			speakerName: constants.speakers.RnMiranda,
		// 			text: `
		// 			Patient name is Andre Garza. He’s a 90-year-old male, 
		// 			70 in, 60 kg. His K is 4.6, and he just ate two hours ago. 
		// 			He is complaining that he feels something in the back of 
		// 			his throat, and he has progressive agitation. He suffers 
		// 			from hypertension, high cholesterol, coronary artery disease, 
		// 			diabetes, and dementia.  His son reports that he has swallowed his dentures in the past.
		// 			`,
		// 			VideoChapter: () =>'DAARC - Scenario3 - intro - 44'
		// 		},
		// 		{
		// 			speakerName: constants.speakers.DrCaleb,
		// 			text: `
		// 				The patient has an 18 gauge IV in place, and his pulse Ox 89% on room air. 						
		// 				I’m already administering epinephrine and steroids. I’ll manage the
		// 				hemodynamics; you manage the airway.
		// 			`,
		// 			VideoChapter: () =>'DAARC - Scenario3 - intro - 44'
		// 		},
		// 		{
		// 			speakerName: constants.speakers.AndreGarza,
		// 			text: `
		// 				I can can’t swallow. There’s something in my throat.
		// 			`,
		// 			VideoChapter: () =>'DAARC - Scenario3 - intro - 44'
		// 		}
		// 	],
		// 	sbarConfig: {
		// 		s: `Andre Garza came in stating that he feels something in the back of his throat, is hoarse, and cannot swallow.`,
		// 		b: `
		// 			Age: 90<br />
		// 			Height (in): 70 in<br />
		// 			Weight (kg): 60 kg<br />
		// 			Potassium (K): 4.6<br />
		// 			NPO Status: He ate two hours ago<br />
		// 			<br />
		// 			PMH: Hypertension, high cholesterol, coronary artery disease (CAD), diabetes, and mild dementia.  
		// 		`,
		// 		a: `He has a possible upper airway obstruction.`,
		// 		r: `His physician is recommending intubation due to the possibility of trauma and edema.`
		// 	}
		// }

		this._id_ScenarioSet(3); // <<<<<< CRITICAL: must be set in every scenario. This number does not always match the "level number" variable set in the declarations above, use the documented level numbers and not how they are listed in game <<<<<<<<

		this.intro.config.part1Config = {
			videoUrl: './assets/videos/scenario3/daarc-scenario3-andregarza-v1.mp4',
			chapters: [
				{
					chapterName: () => 'DAARC - Scenario3 - intro - 53'
				}
			],
			sbarConfig: {
				s: `Andre Garza came in stating that he feels something in the back of his throat, is hoarse, and cannot swallow.`,
				b: `
					Age: 90<br />
					Height (in): 70 in<br />
					Weight (kg): 60 kg<br />
					Potassium (K): 4.6<br />
					NPO Status: He ate two hours ago<br />
					<br />
					PMH: Hypertension, high cholesterol, coronary artery disease (CAD), diabetes, and mild dementia.  
				`,
				b508: `
					Age. 90.
					Height. 70 inches.
					Weight. 60 kilograms.
					Potassium. 4.6.
					NPO Status. He ate two hours ago.
					Past Medical History. Hypertension, high cholesterol, coronary artery disease, diabetes, and mild dementia.  
				`,
				a: `He has a possible upper airway obstruction.`,
				r: `His physician is recommending intubation due to the possibility of trauma and edema.`
			}
		}
	}

	private _setupGeneralNeutralState() {
		this.neutralState.config.part1Config = {
			videoUrl: './assets/videos/scenario3/daarc-scenario3-andregarza-v1.mp4',
			hudIdleVideoChapter: () =>'DAARC - Scenario3 - Flat - Nothing - 1',
			onCalledForVortex: () => {
				this.debriefLog('Called for a Vortex reader');
			}
		};

		var toolsArr = null
		if(this.isLite) {
			toolsArr = [
				constants.toolbagTools.bvm,
				constants.toolbagTools.ett,
				constants.toolbagTools.sga,	
				//constants.toolbagTools.cric //comment this out on cric required scenarios		
			]
		} else {			
				toolsArr = [
				constants.toolbagTools.bvm,
				constants.toolbagTools.ett,
				constants.toolbagTools.sga,
				constants.toolbagTools.cric
			]
		}

		this.neutralState.config.part2Config = {
			videoUrl: './assets/videos/scenario3/daarc-scenario3-andregarza-v1.mp4',
			toolsToChooseFrom: toolsArr,
			onTeamNotDebriefed: () => {
				this.debriefLogFailedItem("You did not brief other staff in the room about attempt order. Briefing your staff on the attempt order will help them anticipate providing the right equipment. Briefings empower your staff to notify you if you fall into fixation error. ")
			},
			onTeamDebriefed: () => {
				this.debriefLog('Briefed other staff in the room about attempt order');
			},
			onToolsOrganized: () => {
				this.debriefLog('Organized equipment');
			}
		};

		this.neutralState.config.part3Config = {
			videoUrl: './assets/videos/scenario3/daarc-scenario3-andregarza-v1.mp4',
			waitForSuctionVideoChapter: () =>'DAARC - Scenario3 - Flat - Nothing - 1',
			onSuctionSet: () => {
				this.debriefLog("Suction set");
			}
		};

		this.neutralState.config.part4Config = {
			videoUrl: './assets/videos/scenario3/daarc-scenario3-andregarza-v1.mp4',
			waitForAdjustBedHeightVideoChapter: () =>'DAARC - Scenario3 - 45 - Nothing - 7',
			onAdjustedBedHeight: () => {
				this._id_bedPos = this._id_bedAdj; // <<<<<<<
				this.debriefLog("Bed Adjusted");
			}
		};

		this.neutralState.config.part5Config = {
			videoUrl: './assets/videos/scenario3/daarc-scenario3-andregarza-v1.mp4',
			liftChinVideoChapter: () =>'DAARC - Scenario3 - 45 - patient-assessment-up - 36',
			chinToChestVideoChapter: () =>'DAARC - Scenario3 - 45 - patient-assessment-down - 37',
			examineNeckVideoChapter: () =>'DAARC - Scenario3 - 45 - patient-assessment-straight - 35',
			stickOutTongueVideoChapter: () =>'DAARC - Scenario3 - 45 - patient-assessment-straight - 35',
			realTeethVideoChapter: () =>'DAARC - Scenario3 - 45 - patient-assessment-straight - 35',
			onAssessmentFinied: () => {
				this.debriefLog("Performed a complete airway assessment")
			}
		};

		this.neutralState.config.part6Config = {
			videoUrl: './assets/videos/scenario3/daarc-scenario3-andregarza-v1.mp4',
			waitForFiberOpticVideoChapter: () => this._id_SegmentAssembly(this._id_preOxText),
			onSetupFiberOptic: () => {
				this.callFlexBr = true;
				this.debriefLog("Called for advanced airway cart")
			},
			notificationText: 'The VL/FBS is available for use.'
		};

		this.neutralState.config.part7Config = {
			videoUrl: './assets/videos/scenario3/daarc-scenario3-andregarza-v1.mp4',
			waitForPreOxygenateVideoChapter: () => this._id_SegmentAssembly(this._id_preOxText), // this needs to update based on the selection made		

			onPreOxygenated: (selection: number) => {
				this.preOxDone = true;
				this._id_UpdatePreOxText(selection); // this should update the pre-oxygenation neutral video segment, but the reference set above might not reflect this update
				switch(selection) {
					case 0:
						this.debriefLog("Pre-Oxygenated with High Flow Nasal Cannula");
						break;
					case 1:
						this.debriefLog("Pre-Oxygenated with Nasal Noninvasive Positive Pressure");
						break;
					case 2:
						this.debriefLog("Pre-Oxygenated with 100% Non-Rebreather Mask");
						break;
					case 3:
						this.debriefLogFailedItem("Pre-Oxygenated with BVM. This may not be the best solution");
						break;
				}
			}
		};

		this.neutralState.config.part8Config = {
			videoUrl: './assets/videos/scenario3/daarc-scenario3-andregarza-v1.mp4',
			meds: [
				{
					title: 'Induction Agent',
					audioPrefix: 'InductionAgent_'
				},
				{
					title: 'Administer Neuromuscular Junction Blocker',
					audioPrefix: 'NMJB_'
				}
			],
			onApneaStartedVideoChapter: () => this._id_SegmentAssembly(this._id_preOxText), // APNEA TIMER INPUT SEGMENT CHANGE HERE
			onPatientFullyMedicated: () => {	
				this.debriefSetAdministeringText("You correctly medicated your patient and earned maximum points.")
			},
			onSingleMedAdministered: () => {
				this.debriefSetAdministeringText("Did you administer all medications that are appropriate, given the patient's condition?") //May need more description			
			}
		};
	}

	public getDefaultSa02() {
		return 89
	}

	public getDefaultApneaTime() {
		return 210;
	}

	private _setupProcedures() {
		this._setupBvm();
		this._setupEtt();
		this._setupSga();
		this._setupCric();
		this._setupAuscultate();
	}

	private _setupBvm() {
		this.procedures.bvm.config.part1Config = {
			isToolEnabled: () => {
				this.uiWrapper.displayGeneralAlertUI({
					title: 'Warning',
					text: 'Assissted BVM generates positive pressure. The patient\'s partial airway obstruction could be converted to a complete airway obstruction.',
					okButton: {
						title: 'OK',
						onClicked: () => {}
					}
				});

				return false;
			},
			videoUrl: './assets/videos/scenario3/daarc-scenario3-andregarza-v1.mp4',
			chooseHandVideoChapter: () => this._id_bvmHandAttempt,
			onChooseHandNextClicked: (isOneHanded: boolean) => {
				if(isOneHanded) {
					this._id_bvmHandAttempt = this._id_SegmentAssembly(this._id_BVMAttempt(1)) // set segment variable equal to this
				} else {
					this._id_bvmHandAttempt = this._id_SegmentAssembly(this._id_BVMAttempt(2)) // set segment variable equal to this
				}
			}
		};
		this.procedures.bvm.config.part2Config = {
			videoUrl: './assets/videos/scenario3/daarc-scenario3-andregarza-v1.mp4',
			onProcedureAttempted: (attemptCount: number) => {
				this.bvmAttemptCount = attemptCount;
				this._CheckOxy();
				//this.addText = "Assisted BVM generates positive pressure.";
				this.debriefLogFailedItem("The BVM should not have been used during this partial airway obstruction due to the positive pressure it causes, which escalated the partial obstruction to a complete airway obstruction.") //this text is not approved, just written using the same style as other patients
				this.showFailedScenarioPopUp('The patient\'s partial airway obstruction became a complete airway obstruction due to the positive pressure from the BVM. Review the debrief and then try again.') //this text is not approved, just written using the same style as other patients
			}
			
		}
	}

	private _setupEtt() {
		this.procedures.ett.config.isAttemptSuccessful = () => {
			return this.eTTCorrect;
		}
		this.procedures.ett.config.ettResistanceConfig = { 
			show: () => !this.eTTCorrect,
			onNextClicked: () => {
			}
		}
		this.procedures.ett.config.part1Config = {
			hideVlButton: () => !this.callFlexBr, // If Neutral step 6 has not been performed, the VL button should be hidden
			videoUrl: './assets/videos/scenario3/daarc-scenario3-andregarza-v1.mp4',
			squeezeAmbuBagVideoChapter: () => this._id_SegmentAssembly(this._id_ETTAttempt()),
			insertingETTVideoChapter: () => this._id_SegmentAssembly(this._id_ETTAttempt()),
			ettSpecialUIConfig: {
				airwayImage: () => {
					if(this.isForceps){
						return './assets/images/ett/Grade1.png';
					} else{
						return './assets/images/ett/Grade1Denture.png';
					}
				},
				magillForceps: {
					isEnabled: true,
					text: () => { 
						if(this.forcepsText){
							return 'Click on the foreign object to remove it from the layrnx.' 
						} else {
							return ''
						}
					},
					onAirwayImageClicked: () => {						
						this.isForceps = true;
						this.eTTCorrect = true;
						//this.auscText = this.auscSuccess;
						this.forcepsText = false;
					},
					onClick: () => {
						this.forcepsText = true;
					},
					
				},
				suction: {
					text: () => ''
				},
				manipulateLarynx: {
					text: () => ''
				}
			},
			onProcedureAttempted: (attemptCount: number) => {
				this.ettAttemptCount = attemptCount
			}						
		};
	}

	private _setupSga() {
		
		this.procedures.sga.config.part1Config = {
			videoUrl: './assets/videos/scenario3/daarc-scenario3-andregarza-v1.mp4',
			squeezeAmbuBagVideoChapter: () =>this._id_SegmentAssembly(this._id_SGAAttempt()),
			insertingSgaVideoChapter: () =>this._id_SegmentAssembly(this._id_SGAAttempt()),
			onProcedureAttempted: (attemptCount: number) => {
				this.sgaAttemptCount = attemptCount
			},			
			
			onMakeAttemptClicked: () => {
				//this.performActionWithWarning("Warning", "There is resistance.")
				this.uiWrapper.displayGeneralAlertUI({
					title: 'Resistance',
					text: 'There is resistance.',
					okButton: {
						title: 'OK',
						onClicked: () => {}
					}
				});
			}
		};
	}

	private _setupCric() {
		this.procedures.cric.config.isAttemptSuccessful = () => {
			return true;
		};
		
		this.procedures.cric.config.part1Config = {
			videoUrl: './assets/videos/scenario3/daarc-scenario3-andregarza-v1.mp4',
			correctToolOrder: [
				'Scalpel',
				'Spreader',
				'Bougie',
				'ETT',
				'Syringe',
			]			
		};
		this.procedures.cric.config.part2Config = {
			videoUrl: './assets/videos/scenario3/daarc-scenario3-andregarza-v1.mp4',
			identifyLandmarksVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(1), 'flat'),			
		};
		this.procedures.cric.config.part3Config = {
			videoUrl: './assets/videos/scenario3/daarc-scenario3-andregarza-v1.mp4'
		};
		this.procedures.cric.config.part4Config = {
			videoUrl: './assets/videos/scenario3/daarc-scenario3-andregarza-v1.mp4',
			scalpelVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(2), 'flat'),
			onMakingIncition: () => {
				this.cricAttemptCount++;
			}						
		};
		this.procedures.cric.config.part5Config = {
			videoUrl: './assets/videos/scenario3/daarc-scenario3-andregarza-v1.mp4',
			spreaderVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(3), 'flat')
		};
		this.procedures.cric.config.part6Config = {
			videoUrl: './assets/videos/scenario3/daarc-scenario3-andregarza-v1.mp4',
			bougieVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(4), 'flat')
		};
		this.procedures.cric.config.part7Config = {
			videoUrl: './assets/videos/scenario3/daarc-scenario3-andregarza-v1.mp4',
			etTubeVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(5), 'flat')
		};
		this.procedures.cric.config.part8Config = {
			videoUrl: './assets/videos/scenario3/daarc-scenario3-andregarza-v1.mp4',
			squeezeAmbuBagVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(6), 'Flat')
		};
	}

	private _setupAuscultate() {
		this.procedures.auscultate.config.part1Config = {
			videoUrl: './assets/videos/scenario3/daarc-scenario3-andregarza-v1.mp4',
			bvmConfig: {
				moveToAuscultateScreenVideoChapter: () => this._id_SegmentAssembly(this._id_bvmAusc),
				checkRiseAndTubeHumidificationText: () => this.auscFailed,
				audioPath: () => {
					return './assets/audio/auscultate/vortexreaderreminder_fail.mp3';
				},
				isAnswerCorrect: (isYesClicked: boolean) => {
					return !isYesClicked;
				},
				auscultateHoverOverMessages: () => {
					return this.hoverBreathNo;
				}
			},
			ettConfig: { 
				moveToAuscultateScreenVideoChapter: () => this._id_SegmentAssembly(this._id_ettAusc),
				checkRiseAndTubeHumidificationText: () => {
					if(this.eTTCorrect) {
						return this.auscSuccess
					} else {
						return this.auscFailed
					}
				},
				audioPath: () => {
					if(this.eTTCorrect) {
						return './assets/audio/auscultate/vortexreaderreminder_success2.mp3';
					} else {
						return './assets/audio/auscultate/vortexreaderreminder_fail.mp3';
					}
				},
				isAnswerCorrect: (isYesClicked: boolean) => {
					if(this.eTTCorrect) {
						return isYesClicked
					} else return !isYesClicked
				},
				auscultateHoverOverMessages: () => {
					if(this.eTTCorrect) {
						return this.hoverBreathYes
					} else {
						return this.hoverBreathNo
					}
				}
			},
			sgaConfig: {
				moveToAuscultateScreenVideoChapter: () => this._id_SegmentAssembly(this._id_sgaAusc),
				checkRiseAndTubeHumidificationText: () => this.auscFailed,
				audioPath: () => {
					return './assets/audio/auscultate/vortexreaderreminder_fail.mp3';
				},
				isAnswerCorrect: (isYesClicked: boolean) => {
					return !isYesClicked;
				},
				auscultateHoverOverMessages: () => {
					return this.hoverBreathNo;
				}
			},
			cricConfig: {
				moveToAuscultateScreenVideoChapter: () => this._id_SegmentAssembly(this._id_cricAusc, 'flat'),
				checkRiseAndTubeHumidificationText: () => this.auscSuccess,
				audioPath: () => {
					return './assets/audio/auscultate/vortexreaderreminder_success2.mp3';
				},
				isAnswerCorrect: (isYesClicked: boolean) => {
					return isYesClicked;
				},				
				auscultateHoverOverMessages: () => {
					return this.hoverBreathYes;
				}
			}
		}
	}


	public getParts(): BaseScenarioPart<DaarcBaseScenario>[] {
		return this.parts;
	}

	public getSoundPath(fileName: string): string {
		return './assets/audio/scenario1/' + fileName + '.ogg';
	}
}